import { Phone, Workflow, Check, X as XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const s = status || "new";
  const cfg = {
    new:          { label: t("adminNotifs.statusNew"),         cls: "bg-slate-500/10 text-slate-200 border-slate-500/50" },
    in_contact:   { label: t("adminNotifs.statusInContact"),   cls: "bg-cyan-500/10 text-cyan-300 border-cyan-500/50" },
    negotiation:  { label: t("adminNotifs.statusNegotiation"), cls: "bg-amber-500/10 text-amber-300 border-amber-500/50" },
    won:          { label: t("adminNotifs.statusWon"),         cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/50" },
    lost:         { label: t("adminNotifs.statusLost"),        cls: "bg-rose-500/10 text-rose-300 border-rose-500/50" },
  }[s] || { label: s.toUpperCase(), cls: "bg-slate-500/10 text-slate-300 border-slate-500/40" };
  return (
    <span className={`text-[10px] label-tech px-2 py-0.5 rounded border ${cfg.cls}`} data-testid={`status-badge-${s}`}>
      {cfg.label}
    </span>
  );
}

export default function AdminNotificationsTab({
  notifs, notifUnread, statusCounts, statusFilter, setStatusFilter,
  editingStatusId, statusDraft, setStatusDraft,
  onOpenEdit, onCancelEdit, onSaveStatus, onMarkRead, onMarkAllRead,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "it";
  return (
    <div className="space-y-3" data-testid="admin-notifications">
      {/* Pipeline filter chips */}
      <div className="flex items-center flex-wrap gap-2 mb-2" data-testid="status-filter-bar">
        {[
          ["", t("adminNotifs.filterAll"), notifs.length],
          ["new", t("adminNotifs.filterNew"), statusCounts.new],
          ["in_contact", t("adminNotifs.filterInContact"), statusCounts.in_contact],
          ["negotiation", t("adminNotifs.filterNegotiation"), statusCounts.negotiation],
          ["won", t("adminNotifs.filterWon"), statusCounts.won],
          ["lost", t("adminNotifs.filterLost"), statusCounts.lost],
        ].map(([key, label, n]) => (
          <button
            key={`f-${key || "all"}`}
            onClick={() => setStatusFilter(key)}
            className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${statusFilter === key ? "border-amber-500/60 text-amber-300 bg-amber-500/10" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}
            data-testid={`filter-status-${key || "all"}`}
          >
            {label} <span className="font-mono-data ml-1 opacity-70">({n})</span>
          </button>
        ))}
      </div>
      {notifs.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-400">
            {t("adminNotifs.summary", { n: notifs.length })} · <span className="text-amber-300">{t("adminNotifs.unread", { n: notifUnread })}</span>
          </div>
          {notifUnread > 0 && (
            <button onClick={onMarkAllRead} className="text-xs text-slate-400 hover:text-amber-300" data-testid="admin-notif-read-all">
              {t("adminNotifs.markAllRead")}
            </button>
          )}
        </div>
      )}
      {notifs.length === 0 ? (
        <div className="tech-card p-8 text-center text-slate-500 text-sm">{t("adminNotifs.empty")}</div>
      ) : (
        <div className="space-y-2">
          {notifs.map((n) => (
            <div
              key={n.id}
              className={`tech-card p-4 ${n.read ? "opacity-70" : "border-amber-500/40"}`}
              data-testid={`admin-notif-${n.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    <span className={`text-[10px] label-tech px-2 py-0.5 rounded border ${n.trigger === "consultation" ? "bg-blue-500/10 text-blue-300 border-blue-500/40" : "bg-amber-500/10 text-amber-300 border-amber-500/40"}`}>
                      {n.trigger === "consultation" ? <span className="flex items-center gap-1"><Phone size={9}/>{t("adminNotifs.consultation")}</span> : t("adminNotifs.investment")}
                    </span>
                    <StatusBadge status={n.status}/>
                    {!n.read && (
                      <span className="text-[10px] text-amber-300 label-tech">{t("adminNotifs.statusNew")}</span>
                    )}
                    <span className="text-xs text-slate-500 font-mono-data">{new Date(n.created_at).toLocaleString(locale)}</span>
                  </div>
                  <div className="font-display font-medium text-slate-100">{n.suggested_category_label}</div>
                  <div className="text-xs text-slate-400 mt-1">
                    {t("adminNotifs.workshop")} <span className="text-slate-200 font-medium">{n.workshop_name || "—"}</span>
                    {n.customer_name && <> · {t("adminNotifs.customer")} <span className="text-slate-200">{n.customer_name}</span></>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{t("adminNotifs.part")} {n.part_description || "—"}</div>
                  {n.rationale && (
                    <div className="mt-2 text-xs text-slate-300 leading-relaxed border-l-2 border-amber-500/40 pl-3 whitespace-pre-wrap">{n.rationale}</div>
                  )}
                  <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-500 font-mono-data">
                    <span>QUOTE {n.quote_id?.slice(0, 8) || "—"}</span>
                    {n.email_status && <span>EMAIL · {n.email_status}</span>}
                  </div>

                  {/* Status history timeline */}
                  {Array.isArray(n.status_history) && n.status_history.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-800/60">
                      <div className="label-tech text-[9px] text-slate-500 mb-1.5 flex items-center gap-1"><Workflow size={9}/> {t("adminNotifs.history")}</div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {n.status_history.slice(-5).reverse().map((h, i) => (
                          <div key={`hist-${i}`} className="flex items-start gap-2 text-[10px] text-slate-500 font-mono-data" data-testid={`status-history-${n.id}-${i}`}>
                            <span className="text-slate-600 shrink-0">{new Date(h.at).toLocaleDateString(locale, { day: "2-digit", month: "2-digit" })}</span>
                            <span className="text-slate-400 shrink-0">{(h.from || "new").toUpperCase()} → <span className="text-amber-300">{(h.to || "").toUpperCase()}</span></span>
                            {h.note && <span className="text-slate-300 italic truncate">&quot;{h.note}&quot;</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Inline status editor */}
                  {editingStatusId === n.id && (
                    <div className="mt-3 p-3 rounded-md bg-slate-900/60 border border-amber-500/30 space-y-2" data-testid={`status-editor-${n.id}`}>
                      <div className="flex items-center gap-2 text-[10px] label-tech text-amber-300"><Workflow size={11}/> {t("adminNotifs.updateStatus")}</div>
                      <div className="flex flex-wrap gap-2">
                        {[
                          ["in_contact", t("adminNotifs.filterInContact"), "border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10"],
                          ["negotiation", t("adminNotifs.filterNegotiation"), "border-amber-500/50 text-amber-300 hover:bg-amber-500/10"],
                          ["won", t("adminNotifs.filterWon"), "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"],
                          ["lost", t("adminNotifs.filterLost"), "border-rose-500/50 text-rose-300 hover:bg-rose-500/10"],
                          ["new", t("adminNotifs.reopen"), "border-slate-500/50 text-slate-300 hover:bg-slate-500/10"],
                        ].map(([val, label, cls]) => (
                          <button
                            key={val}
                            type="button"
                            onClick={() => setStatusDraft((s) => ({ ...s, status: val }))}
                            className={`px-2.5 py-1 rounded-sm text-[11px] border transition-colors ${statusDraft.status === val ? "bg-amber-500/20 border-amber-500/60 text-amber-200" : cls}`}
                            data-testid={`status-option-${val}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        value={statusDraft.note}
                        onChange={(e) => setStatusDraft((s) => ({ ...s, note: e.target.value }))}
                        placeholder={t("adminNotifs.notePlaceholder")}
                        maxLength={500}
                        className="w-full px-3 py-1.5 rounded-sm bg-slate-950 border border-slate-700 text-xs text-slate-200 focus:border-amber-500 focus:outline-none font-mono-data"
                        data-testid={`status-note-input-${n.id}`}
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={onCancelEdit}
                          className="px-3 py-1 rounded-sm text-xs bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 flex items-center gap-1"
                          data-testid={`status-cancel-${n.id}`}
                        >
                          <XIcon size={11}/> {t("common.cancel")}
                        </button>
                        <button
                          type="button"
                          onClick={() => onSaveStatus(n.id)}
                          className="px-3 py-1 rounded-sm text-xs bg-amber-500/20 text-amber-200 hover:bg-amber-500/30 border border-amber-500/50 flex items-center gap-1"
                          data-testid={`status-save-${n.id}`}
                        >
                          <Check size={11}/> {t("common.save")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {editingStatusId !== n.id && (
                    <button
                      onClick={() => onOpenEdit(n)}
                      className="text-[11px] px-2 py-1 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 flex items-center gap-1 whitespace-nowrap"
                      data-testid={`status-edit-${n.id}`}
                    >
                      <Workflow size={10}/> {t("adminNotifs.statusBtn")}
                    </button>
                  )}
                  {!n.read && (
                    <button
                      onClick={() => onMarkRead(n.id)}
                      className="text-xs text-slate-400 hover:text-amber-300 whitespace-nowrap"
                      data-testid={`admin-notif-read-${n.id}`}
                    >
                      {t("common.markRead")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
